import { alpha, Components } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { chipClasses } from '@mui/material/Chip';

const CHIP_COLORS = ['primary', 'secondary', 'error', 'warning', 'info', 'success'] as const;
const CHIP_TINT_OPACITY = 0.1;

// A filled coloured Chip is a tint with shaded text: MUI's solid main with white text fails 4.5:1 for most colours.
const tintedChipVariants = (themeCore: Theme) =>
  CHIP_COLORS.map((color) => {
    const { main, light, dark } = themeCore.palette[color];
    const text = themeCore.palette.mode === 'dark' ? light : dark;

    return {
      props: { color, variant: 'filled' as const },
      style: {
        color: text,
        backgroundColor: alpha(main, CHIP_TINT_OPACITY),
        [`& .${chipClasses.icon}`]: { color: main },
        [`& .${chipClasses.deleteIcon}`]: {
          color: alpha(text, 0.7),
          '&:hover, &:active': { color: text },
        },
        [`&.${chipClasses.clickable}:hover, &.${chipClasses.focusVisible}`]: {
          backgroundColor: alpha(main, CHIP_TINT_OPACITY * 2),
        },
      },
    };
  });

export const overrideComponents = (themeCore: Theme): Components => ({
  MuiContainer: {
    styleOverrides: {
      root: {
        padding: themeCore.spacing(0, 6),
        [themeCore.breakpoints.up('sm')]: {
          padding: themeCore.spacing(0, 6),
        },
      },
    },
    defaultProps: {
      maxWidth: 'xl',
    },
  },
  MuiTab: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        '&.Mui-focusVisible': {
          color: themeCore.palette.primary.light,
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'standard',
      size: 'small',
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        color: themeCore.palette.text.primary,
        '&.Mui-focusVisible': {
          backgroundColor: themeCore.palette.action.hover,
        },
        '&:hover': {
          backgroundColor: themeCore.palette.action.hover,
        },
      },
      sizeSmall: {
        padding: '2px',
      },
      sizeMedium: {
        padding: '4px',
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        '&.Mui-focusVisible': {
          boxShadow: `0 0 0 3px ${alpha(themeCore.palette.primary.main, 0.35)}`,
        },
        '&.MuiButton-text.MuiButton-sizeMedium': {
          padding: '8px 8px',
        },
        '&.MuiButton-outlined.MuiButton-sizeMedium': {
          padding: '7px 16px',
        },
      },
      sizeMedium: {
        padding: '8px 16px',
      },
      startIcon: {
        marginTop: '-5px',
        marginBottom: '-5px',
        '.MuiButton-outlinedSizeMedium &': {
          marginLeft: '-12px',
        },
        '&>.Icon-root': {
          fontSize: 30,
        },
      },
      endIcon: {
        marginTop: '-5px',
        marginBottom: '-5px',
        '.MuiButton-outlinedSizeMedium &': {
          marginRight: '-12px',
        },
        '&>.Icon-root': {
          fontSize: 30,
        },
      },
    },
    defaultProps: {
      disableRipple: true,
      disableElevation: true,
    },
  },
  MuiDialog: {
    styleOverrides: {
      paperWidthMd: {
        maxWidth: 700,
      },
      paperWidthXs: {
        maxWidth: 440,
      },
    },
    defaultProps: {
      maxWidth: 'sm',
      fullWidth: true,
      scroll: 'body',
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        paddingBottom: themeCore.spacing(6),
        overflowWrap: 'anywhere',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        overflowWrap: 'anywhere',
      },
    },
  },
  MuiChip: {
    variants: tintedChipVariants(themeCore),
  },
  MuiDialogContentText: {
    styleOverrides: {
      root: {
        color: themeCore.palette.text.primary,
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        '& .MuiButton-root': {
          letterSpacing: '0.02857em',
          textTransform: 'uppercase',
        },
        '& .MuiButton-sizeMedium': {
          fontSize: '0.875rem',
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        padding: '9px 12px',
      },
    },
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        fontSize: 32,
        margin: '-5px 0',
        color: 'inherit',
      },
    },
  },
  MuiFormControl: {
    defaultProps: {
      variant: 'standard',
    },
  },
  MuiUseMediaQuery: {
    defaultProps: {
      noSsr: true,
    },
  },
  MuiToggleButton: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiStack: {
    defaultProps: {
      useFlexGap: true,
    },
  },
  MuiTimeField: {
    defaultProps: {
      size: 'small',
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      sizeMedium: {
        padding: '8px',
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      color: 'default',
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        padding: '11px',
      },
    },
  },
});
